import React, { useState, useMemo, useEffect } from "react";
import { Line } from "react-chartjs-2";
import CustomDropdown from "../common/CustomDropdown";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CoachStatistics = ({ statistics = [], topFormations = [] }) => {
  const ITEMS_PER_PAGE = 10;

  // Get unique seasons from statistics
  const seasonOptions = useMemo(() => {
    if (!statistics || statistics.length === 0) return [];
    return statistics.map((stat) => ({
      label: stat.season_name || `Season ${stat.season_id}`,
      value: stat.season_id,
    }));
  }, [statistics]);

  // Chart pagination state
  const [chartPage, setChartPage] = useState(0);

  // Default to first season if available
  const [selectedSeasonId, setSelectedSeasonId] = useState(
    seasonOptions.length > 0 ? seasonOptions[0].value : null
  );

  // Update selected season when options change
  useEffect(() => {
    if (seasonOptions.length > 0) {
      // If current selection is invalid or not set, select first option
      const isValidSelection = seasonOptions.some(
        (opt) => opt.value === selectedSeasonId
      );
      if (!isValidSelection || !selectedSeasonId) {
        setSelectedSeasonId(seasonOptions[0].value);
      }
    }
  }, [seasonOptions, selectedSeasonId]);

  // Get selected season stats
  const selectedSeasonStats = useMemo(() => {
    if (!selectedSeasonId || !statistics || statistics.length === 0) return null;
    return statistics.find((stat) => stat.season_id === selectedSeasonId);
  }, [selectedSeasonId, statistics]);

  // Prepare win/loss chart data (all seasons, paginated)
  const allSeasonsData = useMemo(() => {
    if (!statistics || statistics.length === 0) {
      return {
        labels: [],
        wins: [],
        losses: [],
        draws: [],
      };
    }

    // Sort by season name for display
    const sortedStats = [...statistics].sort((a, b) => {
      const nameA = a.season_name || "";
      const nameB = b.season_name || "";
      return nameA.localeCompare(nameB);
    });

    return {
      labels: sortedStats.map((stat) => stat.season_name || `Season ${stat.season_id}`),
      wins: sortedStats.map((stat) => stat.wins || 0),
      losses: sortedStats.map((stat) => stat.losses || 0),
      draws: sortedStats.map((stat) => stat.draws || 0),
    };
  }, [statistics]);

  // Calculate pagination
  const totalPages = Math.ceil(allSeasonsData.labels.length / ITEMS_PER_PAGE);
  const startIndex = chartPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  // Get paginated chart data
  const chartData = useMemo(() => {
    return {
      labels: allSeasonsData.labels.slice(startIndex, endIndex),
      wins: allSeasonsData.wins.slice(startIndex, endIndex),
      losses: allSeasonsData.losses.slice(startIndex, endIndex),
      draws: allSeasonsData.draws.slice(startIndex, endIndex),
    };
  }, [allSeasonsData, startIndex, endIndex]);

  // Calculate total matches for formations (sum of all seasons)
  const totalMatches = useMemo(() => {
    if (!statistics || statistics.length === 0) return 0;
    return statistics.reduce((sum, stat) => sum + (stat.matches || 0), 0);
  }, [statistics]);

  // Pagination handlers
  const handlePrevPage = () => {
    setChartPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setChartPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const lineChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Wins",
        data: chartData.wins,
        borderColor: "#10B981",
        backgroundColor: "#10B981",
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: "Draws",
        data: chartData.draws,
        borderColor: "#F59E0B",
        backgroundColor: "#F59E0B",
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: "Losses",
        data: chartData.losses,
        borderColor: "#EF4444",
        backgroundColor: "#EF4444",
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          boxWidth: 8,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          color: "#000000",
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#000000",
        padding: 12,
        bodySpacing: 4,
        bodyFont: {
          size: 12,
        },
        titleFont: {
          size: 14,
          weight: "bold",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#9CA3AF",
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        min: 0,
        ticks: {
          stepSize: 1,
          color: "#9CA3AF",
        },
        grid: {
          color: "#E5E7EB",
          drawBorder: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  // Helper function to display values (0 shows 0, null shows empty state)
  const displayValue = (value) => {
    if (value === null || value === undefined) return null;
    return value;
  };

  // Stat tile — renders NOTHING for missing values (empty renders nothing,
  // Supports 360px-Audit 2026-08-21: no more "-" placeholders).
  const StatTile = ({ label, value, colorClass = "text-[#222A35]" }) =>
    value === null || value === undefined ? null : (
      <div className="bg-[#F8F9FB] p-4 rounded-lg">
        <p className="text-sm text-[#646B7E] mb-2">{label}</p>
        <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
      </div>
    );

  // Format average points per game
  const formatAvgPoints = (value) => {
    if (value === null || value === undefined) return null;
    return parseFloat(value).toFixed(2);
  };

  // If no statistics available
  if (!statistics || statistics.length === 0) {
    return (
      <div className="space-y-8">
        <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
          <p className="text-[#646B7E] text-center py-8">No statistics available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Content */}
      <div className="space-y-6">
        {/* Top Formations */}
        {topFormations && topFormations.length > 0 ? (
          <div className="bg-white rounded-xl border border-[#D6DBEA]">
            <h2 className="text-[#222A35] text-base font-bold mb-4 p-4 pb-0">COACHING</h2>
            <div className="bg-white py-4 px-4 rounded-lg">
              {/* Mobile (Supports 360px-Audit): label row on top, formation
                  fields stack — the old w-[10%] label clipped to "Top Form…"
                  and the fields bled over the card edge. */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 bg-[#F8F9FB] sm:items-center p-4 rounded-lg border border-[#D6DBEA]">
                <h3 className="text-[#222A35] text-base font-bold sm:flex-shrink-0">Top Formation</h3>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:flex-1">
                  {topFormations.slice(0, 2).map((formation, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg px-4 py-3 border border-gray-200 flex-1"
                    >
                      <div className="text-[#222A35] text-base font-bold">{formation}</div>
                      <div className="text-[#646B7E] text-sm">
                        {totalMatches} matches
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#D6DBEA] p-6">
            <h2 className="text-[#222A35] text-base font-bold mb-4">COACHING</h2>
            <div className="bg-[#F8F9FB] p-4 rounded-lg border border-[#D6DBEA]">
              <p className="text-[#646B7E] text-sm">No formation data available</p>
            </div>
          </div>
        )}

        <h2 className="text-[#222A35] text-base font-bold">STATISTICS</h2>

        {/* Win/Loss Record Chart */}
        {chartData.labels.length > 0 && (
          <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#222A35] text-base font-bold">Win/Loss Record</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={chartPage === 0}
                  className={`p-2 rounded-lg border ${
                    chartPage === 0
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-[#E6E6E6] text-[#222A35] hover:bg-gray-50"
                  }`}
                  aria-label="Previous page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={chartPage >= totalPages - 1}
                  className={`p-2 rounded-lg border ${
                    chartPage >= totalPages - 1
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-[#E6E6E6] text-[#222A35] hover:bg-gray-50"
                  }`}
                  aria-label="Next page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            {/* Mobile: the chart keeps a readable minimum width and SCROLLS
                sideways (UI rule: scroll, never squeeze) — under ~400px the
                fixed-width axes collapsed into the points. */}
            <div className="bg-white p-3 md:p-6 rounded-lg border border-gray-100 overflow-x-auto">
              <div className="min-w-[560px] md:min-w-0 h-[320px] md:h-[400px]">
                <Line data={lineChartData} options={lineChartOptions} />
              </div>
            </div>
          </div>
        )}

        {/* Selected Season Stats */}
        {selectedSeasonStats && (
          <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
            {/* Mobile: title and season dropdown STACK — side by side the
                title squeezed three-lined next to the dropdown. */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="text-[#222A35] text-base font-bold">
                {selectedSeasonStats.season_name || `Season ${selectedSeasonStats.season_id}`}
              </h2>
              <div className="w-full sm:max-w-xs">
                <CustomDropdown
                  options={seasonOptions}
                  value={selectedSeasonId}
                  onChange={setSelectedSeasonId}
                  placeholder="Select season"
                  className="w-full"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                <StatTile label="Matches" value={displayValue(selectedSeasonStats.matches)} />
                <StatTile label="Wins" value={displayValue(selectedSeasonStats.wins)} colorClass="text-[#10B981]" />
                <StatTile label="Draws" value={displayValue(selectedSeasonStats.draws)} colorClass="text-[#F59E0B]" />
                <StatTile label="Losses" value={displayValue(selectedSeasonStats.losses)} colorClass="text-[#EF4444]" />
                <StatTile label="Substitutions" value={displayValue(selectedSeasonStats.substitutions)} />
                <StatTile label="Yellow Cards" value={displayValue(selectedSeasonStats.yellowcards)} colorClass="text-[#F59E0B]" />
                <StatTile label="Yellow-Red Cards" value={displayValue(selectedSeasonStats.yellowred_cards)} colorClass="text-[#F59E0B]" />
                <StatTile label="Red Cards" value={displayValue(selectedSeasonStats.redcards)} colorClass="text-[#EF4444]" />

                {/* Average Points Per Game */}
                {selectedSeasonStats.avg_points_per_game !== null && (
                  <div className="bg-[#F8F9FB] p-4 rounded-lg">
                    <p className="text-sm text-[#646B7E] mb-2">Avg Points/Game</p>
                    <p className="text-2xl font-bold text-[#222A35]">
                      {formatAvgPoints(selectedSeasonStats.avg_points_per_game)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* No season selected state */}
        {!selectedSeasonStats && (
          <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
            <p className="text-[#646B7E] text-center py-8">Select a season to view statistics</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachStatistics;

